import React, { useEffect, useState, useCallback, useRef } from 'react';
import { EyeOff, Eye, Trash2, Check, X, FileText, Paperclip, AlertCircle, Loader2, Eraser } from 'lucide-react';
import { redactSelection } from '../../utils/redactSelection';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Gangland', 'Tobacco War', 'Drug Network', 'AFL Scandal', 'Corruption', 'Crime Scene', 'Other'];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

const TipRow = ({ tip, token, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [redacted, setRedacted] = useState(tip.redacted_text || tip.tip_text || '');
  const [category, setCategory] = useState(tip.category || 'Other');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const redactedRef = useRef(null);

  const publish = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await fetch(`${API}/api/admin/tips/${tip.id}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ redacted_text: redacted.trim(), category }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setEditing(false);
      onChange?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const unpublish = async () => {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/tips/${tip.id}/unpublish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange?.();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Permanently delete this tip record? (Email already sent is unaffected.)')) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/tips/${tip.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange?.();
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border p-4 ${tip.is_public ? 'border-emerald-500/40 bg-emerald-950/15' : 'border-zinc-800 bg-zinc-900/60'}`}
         data-testid={`admin-tip-row-${tip.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-widest text-red-400 font-black uppercase">
            {tip.category || 'General'}
            {tip.alias && <span className="ml-2 text-gray-400 lowercase font-normal normal-case">· alias: {tip.alias}</span>}
          </p>
          <p className="text-[10px] text-gray-500">{fmtDate(tip.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          {tip.is_public ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
              <Eye className="h-3 w-3" /> On Wall
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider bg-zinc-800 text-gray-400 border border-zinc-700">
              <EyeOff className="h-3 w-3" /> Private
            </span>
          )}
          {tip.attachment_url && (
            <>
              <a
                href={tip.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-tip-attachment-${tip.id}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider bg-zinc-800 text-gray-300 hover:bg-zinc-700 border border-zinc-700"
              >
                <Paperclip className="h-3 w-3" /> Attachment
              </a>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Remove this attachment from the tip? The file will remain in storage but no longer appear on the tip.')) return;
                  try {
                    const res = await fetch(`${API}/api/admin/tips/${tip.id}/attachment`, {
                      method: 'DELETE',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) throw new Error();
                    onChange?.();
                  } catch { alert('Could not remove attachment'); }
                }}
                data-testid={`admin-tip-remove-attachment-${tip.id}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider bg-red-950/40 text-red-300 hover:bg-red-900/50 border border-red-900/50"
                title="Remove attachment"
              >
                <X className="h-3 w-3" /> Remove
              </button>
            </>
          )}
        </div>
      </div>

      {!editing && (
        <p className="text-sm text-gray-200 whitespace-pre-wrap break-words border-l-2 border-red-700/60 pl-3 my-2">
          {tip.tip_text}
        </p>
      )}

      {tip.is_public && !editing && tip.redacted_text && (
        <div className="mt-3 p-3 rounded bg-emerald-950/30 border border-emerald-500/30">
          <p className="text-[10px] tracking-widest text-emerald-400 font-black uppercase mb-1">Public version (shown on /wall)</p>
          <p className="text-sm text-emerald-100 whitespace-pre-wrap break-words">{tip.redacted_text}</p>
        </div>
      )}

      {editing && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid={`admin-tip-category-${tip.id}`}
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">
              Redacted text (this is what visitors will see)
            </label>
            <textarea
              ref={redactedRef}
              value={redacted}
              onChange={(e) => setRedacted(e.target.value)}
              data-testid={`admin-tip-redacted-${tip.id}`}
              rows={4}
              maxLength={2000}
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none resize-y"
            />
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const next = redactSelection(redactedRef.current, redacted);
                  if (next != null) { setRedacted(next); setErr(''); }
                  else setErr('Highlight the word or name you want to redact first.');
                }}
                data-testid={`admin-tip-redact-selection-${tip.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-yellow-700/50 hover:border-yellow-500 text-yellow-300 text-[10px] font-black uppercase tracking-widest"
                title="Highlight a name then click to whiteout it"
              >
                <Eraser className="h-3 w-3" /> Redact selection
              </button>
              <p className="text-[10px] text-gray-500">
                or type <span className="font-mono font-black text-yellow-300">XXXX</span> manually · {redacted.length} / 2,000
              </p>
            </div>
          </div>
          {err && (
            <div className="text-[11px] text-red-400 flex items-center gap-1.5">
              <AlertCircle className="h-3 w-3" /> {err}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {!editing && !tip.is_public && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`admin-tip-edit-${tip.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-widest"
          >
            <Eye className="h-3.5 w-3.5" />
            Publish to Wall
          </button>
        )}
        {!editing && tip.is_public && (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[11px] font-black uppercase tracking-widest"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={unpublish}
              disabled={busy}
              data-testid={`admin-tip-unpublish-${tip.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-yellow-700/60 hover:bg-yellow-700 text-white text-[11px] font-black uppercase tracking-widest"
            >
              <EyeOff className="h-3.5 w-3.5" />
              Unpublish
            </button>
          </>
        )}
        {editing && (
          <>
            <button
              type="button"
              onClick={publish}
              disabled={busy || redacted.trim().length < 4}
              data-testid={`admin-tip-save-${tip.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-[11px] font-black uppercase tracking-widest"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {tip.is_public ? 'Save & Re-publish' : 'Publish'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setErr(''); setRedacted(tip.redacted_text || tip.tip_text || ''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[11px] font-black uppercase tracking-widest"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </>
        )}
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          data-testid={`admin-tip-delete-${tip.id}`}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300 text-[11px] font-black uppercase tracking-widest"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </button>
      </div>
    </div>
  );
};

const TipWallTab = ({ token }) => {
  const [filter, setFilter] = useState('all');
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/tips?filter_state=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTips(data.tips || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filter, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="tip-wall-tab">
      <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="h-5 w-5 text-red-500" />
          <h3 className="text-xl font-black text-white">Tip Wall Moderation</h3>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Tips arrive privately via email + are mirrored here. Pick the ones worth publishing,
          write a redacted public version, and they'll show up on <code className="text-red-300">/wall</code>.
        </p>

        <div className="inline-flex items-center gap-1 rounded-lg bg-zinc-950/60 p-1 mb-4">
          {[
            { id: 'all', label: 'All' },
            { id: 'private', label: 'Private' },
            { id: 'public', label: 'On Wall' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`tip-wall-filter-${f.id}`}
              className={`px-3 py-1.5 rounded text-[11px] font-black uppercase tracking-widest transition-colors ${
                filter === f.id ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-950/40 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm mb-4">
            {error}
          </div>
        )}

        {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}

        {!loading && tips.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-10">No tips in this view.</div>
        )}

        <div className="space-y-3">
          {tips.map((t) => (
            <TipRow key={t.id} tip={t} token={token} onChange={load} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TipWallTab;
